import { Component, NgZone, OnDestroy, ViewEncapsulation, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { LoaderService } from '../../services/loader/loader.service';
import { CardLoaderComponent } from '../card-loader/card-loader.component';

/**
 * The app's loading screen, shown while LoaderService has a request in flight. It covers the page's white content
 * area, never the header or the nav: solid white over a page that is still empty and a soft see-through wash over a
 * page that has content (the card loader sits on a small chip then). Styles are global (no encapsulation) so
 * CardLoaderComponent can react to this overlay's classes.
 */
@Component({
  selector: 'app-loading-overlay',
  standalone: true,
  imports: [AsyncPipe, CardLoaderComponent],
  templateUrl: './loading-overlay.component.html',
  styleUrls: ['./loading-overlay.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoadingOverlayComponent implements OnDestroy {
  readonly loader = inject(LoaderService);
  private zone = inject(NgZone);
  private frame = 0;

  constructor() {
    this.loader.isVisible$.subscribe(visible => {
      cancelAnimationFrame(this.frame);
      if (visible) this.track();
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
  }

  /**
   * Keeps the loader over the page's white content area (below the header, beside the web gutters, clear of the
   * tab bar) for as long as it is showing. It follows the page frame by frame, so it stays put while a page slides
   * in or out (back from a live game) or the window scrolls, instead of keeping the position it had when it appeared.
   */
  private track(): void {
    this.zone.runOutsideAngular(() => {
      const step = () => {
        const loader = document.querySelector<HTMLElement>('.loading-overlay');
        const box = this.measure();
        if (loader && box) {
          const style = loader.style;
          style.top = `${box.top}px`;
          style.left = `${box.left}px`;
          style.width = `${box.width}px`;
          style.height = `${box.height}px`;
          style.borderRadius = box.radius;
          style.right = 'auto';
          style.bottom = 'auto';
          loader.classList.toggle('over-content', box.hasContent);
        }
        this.frame = requestAnimationFrame(step);
      };
      step();
    });
  }

  private measure() {
    // While a page slides in, the one it replaces is still there: take the one most on screen.
    const contents = Array.from(document.querySelectorAll<HTMLElement>('.ion-page:not(.ion-page-hidden) > ion-content'));
    const onScreen = (r: DOMRect) =>
      Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0)) * Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
    let content: HTMLElement | undefined;
    let best = 0;
    for (const c of contents) {
      const area = onScreen(c.getBoundingClientRect());
      if (area >= best && area > 0) {
        best = area;
        content = c;
      }
    }
    if (!content) return null;

    // The white sheet is the content's background part, which on web is narrower than the content itself.
    const background = content.shadowRoot?.querySelector<HTMLElement>('[part="background"]');
    const rect = (background ?? content).getBoundingClientRect();
    let top = Math.max(rect.top, 0);
    let bottom = Math.min(rect.bottom, innerHeight);
    const tabBar = document.querySelector('ion-tab-bar')?.getBoundingClientRect();
    if (tabBar && tabBar.height > 0) {
      if (tabBar.top > innerHeight / 2) {
        bottom = Math.min(bottom, tabBar.top);
      } else {
        top = Math.max(top, tabBar.bottom);
      }
    }
    const left = Math.max(rect.left, 0);
    const right = Math.min(rect.right, innerWidth);
    // A page with something to show has more in it than a header spacer; the loader is a see-through wash over that,
    // and solid white over a page that is still empty.
    const first = content.firstElementChild?.getBoundingClientRect();
    const last = content.lastElementChild?.getBoundingClientRect();
    const hasContent = !!first && !!last && last.bottom - first.top > 160;
    return {
      hasContent,
      top,
      left,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
      radius: background ? getComputedStyle(background).borderRadius : '0'
    };
  }
}
